import type { StageForecast } from '@mustauto/types';
import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { PaintReadings, VehicleSize, WorkshopModule } from '@mustauto/types';
import type { OSWarranty } from '@mustauto/utils';
import { api } from '@/lib/api';

// ── Types ──

export interface OSListItem {
  id: string;
  number: number;
  status: string;
  plate: string;
  vehicle: string;
  mechanicName: string;
  deadline: string | null;
  createdAt: string;
  /** Opcionais: painel e API sobem em deploys separados. */
  module?: string | null;
  stage?: string | null;
}

interface OSListResponse {
  orders: OSListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface OSFilters {
  status?: string;
  mechanicId?: string;
  from?: string;
  to?: string;
  plate?: string;
  number?: number;
  /** Só as OS paradas — mesmo critério da tela de alertas e do resumo da manhã. */
  stale?: boolean;
  page?: number;
  limit?: number;
}

export interface OSItem {
  /** Identidade do item entre edições — o app sempre manda; o painel passou a mandar (o servidor confere o preço do item novo, `os.preco_manual`). */
  id?: string;
  type: 'part' | 'labor' | 'outsourced';
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  /** Código/SKU da peça. */
  code?: string | null;
  /**
   * `origin` + `inventoryItemId` são o vínculo com o estoque, e é esse par que
   * `deductInventoryForOS` exige para debitar na conclusão da OS. Precisam
   * sobreviver a toda edição de itens no painel: reconstruir o item só com
   * descrição, quantidade e preço apaga o vínculo e a peça deixa de baixar.
   */
  origin?: 'stock' | 'supplier' | 'customer_brings';
  inventoryItemId?: string | null;
  /**
   * Peça do carro, serviço e procedência (orçamento por peça, F3.3). Chaves de
   * `@mustauto/types`; `''` só existe no rascunho da edição, como "nenhuma".
   */
  panel?: string;
  operation?: string;
  partOrigin?: string;
  /**
   * Sinistro (Fase 4): a seguradora cobre ou recusou o item. Sem marca é
   * coberto; `''` só existe no rascunho da edição.
   */
  insuranceCoverage?: 'covered' | 'declined' | '';
}

/** Sinistro de seguradora da OS (Fase 4), como a API o devolve. Valores em REAIS. */
export interface InsuranceClaim {
  id: string;
  insurer: string;
  insurerName: string | null;
  insurerLabel: string;
  claimNumber: string | null;
  deductible: number;
  authorizedAt: string | null;
  dueDate: string | null;
  effectiveDueDate: string | null;
  notes: string | null;
  covered: number;
  declined: number;
  deductibleApplied: number;
  insurerDue: number;
  insurerPaid: number;
  insurerRemaining: number;
  insurerStatus: 'nothing_due' | 'awaiting_service' | 'open' | 'partial' | 'overdue' | 'paid';
  payments: { id: string; amount: number; paidAt: string; method: string | null; notes: string | null }[];
}

export interface InsuranceClaimInput {
  insurer: string;
  insurerName: string | null;
  claimNumber: string | null;
  deductible: number;
  authorizedAt: string | null;
  dueDate: string | null;
  notes: string | null;
}

export interface InsurerPaymentInput {
  amount: number;
  paidAt: string;
  method: string | null;
  notes: string | null;
}

export interface OSDetail {
  id: string;
  number: number;
  status: string;
  plate: string;
  make: string;
  model: string;
  vehicleName: string;
  vehicleId: string;
  mechanicId: string;
  mechanicName: string | null;
  mechanicPhone: string | null;
  customerId: string | null;
  customerName: string | null;
  customerPhone: string | null;
  year: number;
  items: OSItem[];
  deadline: string | null;
  totalParts: string;
  totalLabor: string;
  totalOutsourced: string;
  paidAt: string | null;
  paidAmount: string | null;
  paymentMethod: string | null;
  slotId: string | null;
  slotLabel: string | null;
  notes: string | null;
  /** Anotações internas — só a oficina vê. Editáveis até a entrega. */
  internalNotes: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Se o gestor ainda pode reabrir a OS fechada por engano, para onde ela volta
   * e até quando (48h do fechamento). `null` = não dá — a API decide.
   */
  reopen: { targetStatus: string; until: string } | null;
  /** Módulo da OS (`null` = sem tipo: OS antiga ou oficina de um módulo só). */
  module?: string | null;
  /** Etapa dentro do módulo. */
  stage?: string | null;
  /** Cor da pintura e código do fabricante (avaliação de dano da funilaria). */
  paintColor?: string | null;
  paintCode?: string | null;
  /** Fórmula de mistura da tinta (F3.6) — só desta OS. */
  paintFormula?: string | null;
  /** Código de cor do cadastro do carro, para sugerir quando a OS não tem. */
  vehiclePaintCode?: string | null;
  /** Previsão da etapa atual (F3.7); `null` sem duração configurada. */
  stageForecast?: StageForecast | null;
  /**
   * Sinistro de seguradora (Fase 4). Com ele, `totalDue`/`remaining` são a
   * parte do CLIENTE (franquia + recusados − desconto).
   */
  insuranceClaim?: InsuranceClaim | null;
  /** Porte com que o pacote de estética foi cobrado (Fase 2). */
  vehicleSize?: VehicleSize | null;
  /** Garantia do pacote (F2.5): datas puras `YYYY-MM-DD`. */
  warranty?: OSWarranty | null;
  /** Laudo de espessura da pintura (F2.3), ou `null`. */
  paintReadings?: PaintReadings | null;
  /** Insumos do pacote (F2.6): receita + ajuste; o que baixa na conclusão é `quantity`. */
  consumables?: OSConsumable[];
  /** Os insumos já baixaram (fechada, ou concluída e reaberta): o ajuste não muda mais. */
  consumablesLocked?: boolean;
  totalDue?: number;
  remaining?: number;
}

export interface OSConsumable {
  inventoryItemId: string;
  name: string;
  unit: string | null;
  recipeQuantity: number;
  quantity: number;
  adjusted: boolean;
  available: number;
}

export interface PaintInput {
  paintColor: string | null;
  paintCode: string | null;
  paintFormula: string | null;
}

interface CreateOSData {
  plate: string;
  make: string;
  model: string;
  yearModel: number;
  vehicleId?: string;
  yearManuf?: number;
  engineType?: string;
  color?: string;
  mechanicId: string;
  /** Obrigatória, menos com pacote (F2.2) — aí é observação do cliente. */
  complaint?: string;
  notes?: string;
  deadline?: string;
  items?: Omit<OSItem, 'total'>[];
  customerPhone?: string;
  customerName?: string;
  /** Só quando a oficina tem escolha (`openingModuleChoice`). */
  module?: WorkshopModule;
  /** Pacotes de estética (F2.2) — a API monta os itens com o preço do porte. */
  packageIds?: string[];
  vehicleSize?: VehicleSize | null;
}

// ── List hook ──

export function useServiceOrders(filters: OSFilters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);
  if (filters.mechanicId) params.set('mechanicId', filters.mechanicId);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.plate) params.set('plate', filters.plate);
  if (filters.number !== undefined) params.set('number', String(filters.number));
  if (filters.stale) params.set('stale', 'true');
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  const qs = params.toString();

  return useQuery<OSListResponse>({
    queryKey: ['service-orders', filters],
    queryFn: async () => {
      const res = await api.get(`/service-orders${qs ? `?${qs}` : ''}`);
      return res.data;
    },
  });
}

// ── Detail hook with mutations ──

/**
 * O que relê depois de mudar o status de uma OS — o seletor do detalhe e o
 * "Entregar veículo" passam pelos dois. Também no erro: um 409 quer dizer que
 * a OS mudou (outro aparelho entregou ou reabriu), e a tela precisa mostrar o
 * estado real. Não conta com o WebSocket (`useOSRealtime`), que pode estar
 * reconectando.
 */
function invalidateAfterStatusChange(queryClient: QueryClient, osId: string) {
  void queryClient.invalidateQueries({ queryKey: ['service-orders'] });
  void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  void queryClient.invalidateQueries({ queryKey: ['audit-log', osId] });
  return queryClient.invalidateQueries({ queryKey: ['service-order', osId] });
}

export function useServiceOrder(osId: string) {
  const queryClient = useQueryClient();

  const query = useQuery<OSDetail>({
    queryKey: ['service-order', osId],
    queryFn: async () => {
      const res = await api.get(`/service-orders/${osId}`);
      return res.data;
    },
    enabled: !!osId,
  });

  const statusMutation = useMutation({
    mutationFn: async (data: { status: string; notes?: string }) => {
      const res = await api.patch(`/service-orders/${osId}/status`, data);
      return res.data;
    },
    onSettled: () => invalidateAfterStatusChange(queryClient, osId),
  });

  const itemsMutation = useMutation({
    mutationFn: async (items: Omit<OSItem, 'total'>[]) => {
      const res = await api.patch(`/service-orders/${osId}/items`, { items });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-order', osId] });
      queryClient.invalidateQueries({ queryKey: ['service-orders'] });
    },
  });

  const stageMutation = useMutation({
    mutationFn: async (stage: string | null) => {
      const res = await api.patch(`/service-orders/${osId}/stage`, { stage });
      return res.data as { id: string; module: string | null; stage: string | null };
    },
    // Também no erro: 409 é a OS que fechou ou mudou de etapa em outro
    // aparelho, e o seletor precisa voltar ao valor real. Devolver a promessa
    // da OS faz o `mutateAsync` esperar a releitura — o seletor segura a
    // escolha até lá, e sem isso piscaria de volta para a etapa anterior.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['audit-log', osId] });
      return queryClient.invalidateQueries({ queryKey: ['service-order', osId] });
    },
  });

  const deadlineMutation = useMutation({
    mutationFn: async (deadline: string) => {
      const res = await api.patch(`/service-orders/${osId}/deadline`, { deadline });
      return res.data;
    },
    // A lista mostra o prazo e o histórico ganha a linha; no erro (409, a OS
    // fechou enquanto a tela estava aberta) a tela relê o estado real.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['audit-log', osId] });
      return queryClient.invalidateQueries({ queryKey: ['service-order', osId] });
    },
  });

  // Anotações internas: o histórico ganha a linha com o texto de antes; no
  // erro (409, a OS foi entregue com a tela aberta) a tela relê o estado real.
  const internalNotesMutation = useMutation({
    mutationFn: async (internalNotes: string | null) =>
      (await api.patch(`/service-orders/${osId}/internal-notes`, { internalNotes })).data as {
        internalNotes: string | null;
      },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['audit-log', osId] });
      return queryClient.invalidateQueries({ queryKey: ['service-order', osId] });
    },
  });

  const paintMutation = useMutation({
    mutationFn: async (paint: PaintInput) => {
      const res = await api.patch(`/service-orders/${osId}/paint`, paint);
      return res.data as PaintInput;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['audit-log', osId] });
      return queryClient.invalidateQueries({ queryKey: ['service-order', osId] });
    },
  });

  // Trocar a vaga (o carro mudou de elevador). A chave `service-orders` relê
  // também "Carros na oficina"; 409 é a vaga ocupada por outra OS, e a
  // mensagem da API diz qual.
  const slotMutation = useMutation({
    mutationFn: async (slotId: string | null) =>
      (await api.patch(`/service-orders/${osId}/slot`, { slotId })).data as { slotId: string | null; slotLabel: string | null },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['service-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['slots-status'] });
      void queryClient.invalidateQueries({ queryKey: ['audit-log', osId] });
      return queryClient.invalidateQueries({ queryKey: ['service-order', osId] });
    },
  });

  // Corrigir placa e modelo. A chave `service-orders` relê a lista (que mostra
  // a placa); 409 é o cadastro compartilhado, e a mensagem da API diz por quê.
  const vehicleMutation = useMutation({
    mutationFn: async (vehicle: { plate: string; make: string; model: string; yearModel: number }) =>
      (await api.patch(`/service-orders/${osId}/vehicle`, vehicle)).data as {
        plate: string;
        make: string;
        model: string;
        yearModel: number;
        vehicleId: string;
        outcome: 'unchanged' | 'renamed' | 'moved' | 'created' | 'edited';
      },
    onSuccess: async (data) => {
      if (data.outcome === 'unchanged') return;
      void queryClient.invalidateQueries({ queryKey: ['service-orders'] });
      void queryClient.invalidateQueries({ queryKey: ['audit-log', osId] });
      await queryClient.invalidateQueries({ queryKey: ['service-order', osId] });
    },
  });

  // Sinistro (Fase 4). Toda escrita relê a OS: a franquia muda o que o
  // cliente deve, e o histórico ganha a linha.
  const refreshOS = () => {
    void queryClient.invalidateQueries({ queryKey: ['audit-log', osId] });
    void queryClient.invalidateQueries({ queryKey: ['finance', 'insurer-receivables'] });
    return queryClient.invalidateQueries({ queryKey: ['service-order', osId] });
  };
  const claimMutation = useMutation({
    mutationFn: async (claim: InsuranceClaimInput) => (await api.put(`/service-orders/${osId}/insurance-claim`, claim)).data,
    onSuccess: refreshOS,
  });
  const removeClaimMutation = useMutation({
    mutationFn: async () => api.delete(`/service-orders/${osId}/insurance-claim`),
    onSuccess: refreshOS,
  });
  const insurerPaymentMutation = useMutation({
    mutationFn: async (payment: InsurerPaymentInput) =>
      (await api.post(`/service-orders/${osId}/insurance-claim/payments`, payment)).data,
    onSuccess: refreshOS,
  });
  const removeInsurerPaymentMutation = useMutation({
    mutationFn: async (paymentId: string) => api.delete(`/service-orders/${osId}/insurance-claim/payments/${paymentId}`),
    onSuccess: refreshOS,
  });

  return {
    os: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
    updateStatus: statusMutation.mutateAsync,
    isUpdatingStatus: statusMutation.isPending,
    updateItems: itemsMutation.mutateAsync,
    isUpdatingItems: itemsMutation.isPending,
    updateDeadline: deadlineMutation.mutateAsync,
    isUpdatingDeadline: deadlineMutation.isPending,
    updateInternalNotes: internalNotesMutation.mutateAsync,
    isUpdatingInternalNotes: internalNotesMutation.isPending,
    updateStage: stageMutation.mutateAsync,
    isUpdatingStage: stageMutation.isPending,
    updatePaint: paintMutation.mutateAsync,
    isUpdatingPaint: paintMutation.isPending,
    updateSlot: slotMutation.mutateAsync,
    isUpdatingSlot: slotMutation.isPending,
    updateVehicle: vehicleMutation.mutateAsync,
    isUpdatingVehicle: vehicleMutation.isPending,
    saveClaim: claimMutation.mutateAsync,
    removeClaim: removeClaimMutation.mutateAsync,
    addInsurerPayment: insurerPaymentMutation.mutateAsync,
    removeInsurerPayment: removeInsurerPaymentMutation.mutateAsync,
    isSavingClaim:
      claimMutation.isPending ||
      removeClaimMutation.isPending ||
      insurerPaymentMutation.isPending ||
      removeInsurerPaymentMutation.isPending,
  };
}

// ── Audit log hook ──

export interface AuditLogEntry {
  id: string;
  action: string;
  fromValue: string | null;
  toValue: string | null;
  reason: string | null;
  actorName: string;
  createdAt: string;
}

export function useAuditLog(osId: string) {
  return useQuery<{ entries: AuditLogEntry[] }>({
    queryKey: ['audit-log', osId],
    queryFn: async () => {
      const res = await api.get(`/service-orders/${osId}/audit-log`);
      return res.data;
    },
    enabled: !!osId,
  });
}

// ── Uncancel hook ──

export function useUncancelOS(osId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: { reason: string }) => {
      const res = await api.post(`/service-orders/${osId}/uncancel`, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-order', osId] });
      queryClient.invalidateQueries({ queryKey: ['service-orders'] });
      queryClient.invalidateQueries({ queryKey: ['audit-log', osId] });
    },
  });

  return {
    uncancel: mutation.mutateAsync,
    isUncancelling: mutation.isPending,
    error: mutation.error,
  };
}

// ── Reopen hook ──

export function useReopenOS(osId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: { reason: string }) => {
      const res = await api.post(`/service-orders/${osId}/reopen`, data);
      return res.data as { ok: true; status: string };
    },
    onSettled: () => {
      // Também no erro: um 409 quer dizer que a OS mudou ou a janela passou,
      // e a tela precisa mostrar o estado real.
      queryClient.invalidateQueries({ queryKey: ['service-order', osId] });
      queryClient.invalidateQueries({ queryKey: ['service-orders'] });
      queryClient.invalidateQueries({ queryKey: ['audit-log', osId] });
    },
  });

  return {
    reopen: mutation.mutateAsync,
    isReopening: mutation.isPending,
  };
}

// ── Deliver hook ──

/**
 * "Entregar veículo" — concluída → entregue. É a mesma rota de status do
 * seletor, mas a OS concluída é só leitura no detalhe, e sem este caminho o
 * painel não tinha onde marcar a entrega: o carro ficava em "Carros na
 * oficina" até o app do mecânico entregar. Fica fora do hook da OS porque a
 * lista de carros entrega sem ter a OS carregada.
 */
export function useDeliverOS(osId: string) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.patch(`/service-orders/${osId}/status`, { status: 'delivered' });
      return res.data;
    },
    onSettled: () => invalidateAfterStatusChange(queryClient, osId),
  });

  return {
    deliver: mutation.mutateAsync,
    isDelivering: mutation.isPending,
  };
}

// ── Create hook ──

export function useCreateServiceOrder() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data: CreateOSData) => {
      const res = await api.post('/service-orders', data);
      return res.data as { id: string; number: number };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-orders'] });
    },
  });

  return {
    create: mutation.mutateAsync,
    isCreating: mutation.isPending,
    error: mutation.error,
  };
}
